import { Header, NotificationHub, RichTable } from "@nef/core"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import marketHolidayColumns from "../../constants/table/marketHolidayColumns"
import { MarketHoliday } from "../../types/MarketHoliday"
import useGetColumnMap from "../../hooks/useGetColumnMap"
import { DELETE_MODAL_PROPS, HEADERS, TABLE_EMPTY_API_DATA, TABLE_EMPTY_STATE_MESSAGE } from "../../constants/common"
import TableDownloadDropdown from "../../components/common/TableDownloadDropdown"
import { FileExtensions } from "../../types/_enums"
import { AppDispatch } from "../../app/store"
import { fetchMarketCloseDays, fetchMarketCloseDaysCustom, removeMarketCloseDay } from "../../redux/utilities/actions"
import { downloadCSVFromString } from "../../utils/csvHandler"
import { DeleteModalProps } from "../../types/common"
import { dateFormatForDisplay } from "../../utils/datetimehandler"
import DeleteConfirmationModal from "../../modals/common/DeleteConfirmationModal"

const MarketHolidays = ({ nasdaqHolidays, customHolidays, getMarketHolidays, isCalendarFetching, viewOnly = false }: any) => {
  const [nmcHiddenColumns, nmcUpdateLocalStorage, nmcHandleColumnSelection] = useGetColumnMap("nasdaqMarketCloseDays")
  const [customHiddenColumns, customUpdateLocalStorage, customHandleColumnSelection] = useGetColumnMap("customMarketCloseDays")
  const [isNasdaqHolidaysFileDownloading, setIsNasdaqHolidaysFileDownloading] = useState<boolean>(false)
  const [isCustomHolidaysFileDownloading, setIsCustomHolidaysFileDownloading] = useState<boolean>(false)
  const dispatch = useDispatch<AppDispatch>()
  const [deleteModalProps, setDeleteModalProps] = useState<DeleteModalProps>(DELETE_MODAL_PROPS)

  const handleTableExport = async (type: FileExtensions, table: string) => {
    const payload = {
      requestHeaders: HEADERS[type],
      params: { year: new Date().getFullYear() }
    }
    try {
      let res
      if (table === "nasdaq-market-holidays") {
        setIsNasdaqHolidaysFileDownloading(true)
        res = await dispatch(fetchMarketCloseDays(payload)).unwrap()
      } else {
        setIsCustomHolidaysFileDownloading(true)
        res = await dispatch(fetchMarketCloseDaysCustom(payload)).unwrap()
      }

      if (!res) {
        NotificationHub.send("primary", `No data available to export!`)
        setIsNasdaqHolidaysFileDownloading(false)
        setIsCustomHolidaysFileDownloading(false)
        return
      }
      downloadCSVFromString(res, `${table}.${type}`, type)
      setIsNasdaqHolidaysFileDownloading(false)
      setIsCustomHolidaysFileDownloading(false)
    } catch (error: any) {
      setIsNasdaqHolidaysFileDownloading(false)
      setIsCustomHolidaysFileDownloading(false)
      NotificationHub.send("danger", `${error.status} ${error.message}`)
    }
  }

  // handler for the del button click on the custom grid
  const handleDateDelete = (selectedDate: MarketHoliday) => {
    // trigger a confirmation modal
    setDeleteModalProps({
      isOpen: true,
      toggle: () => setDeleteModalProps(DELETE_MODAL_PROPS),
      info: `Are you sure you want to delete ${dateFormatForDisplay(selectedDate.calendarDate)}?`,
      onConfirm: () => deleteCustomDate(selectedDate),
      isLoading: false
    })
  }

  // api to del the custom date
  const deleteCustomDate = async (date: MarketHoliday) => {
    const params = {
      closedMarketDateId: date.marketCalendarId
    }
    try {
      setDeleteModalProps((prev: DeleteModalProps) => ({ ...prev, isLoading: true }))
      await dispatch(removeMarketCloseDay({ params })).unwrap()
      setDeleteModalProps(DELETE_MODAL_PROPS)
      NotificationHub.send("success", "The custom close date has been deleted!")
      getMarketHolidays()
    } catch (error: any) {
      setDeleteModalProps((prev: DeleteModalProps) => ({ ...prev, isLoading: false }))
      NotificationHub.send("danger", `${error.status} ${error.message}`)
    }
  }

  useEffect(() => {
    // fetch holidays
    if (!nasdaqHolidays?.length) getMarketHolidays()
    return () => {
      nmcUpdateLocalStorage()
      customUpdateLocalStorage()
    }
  }, [])

  return (
    <>
      <DeleteConfirmationModal {...deleteModalProps} />
      <div className="mc-calendar__section">
        <div className="mc-calendar__card">
          <RichTable
            className="mc-calendar__rich-table"
            bodyHeight={430}
            options={{
              columns: marketHolidayColumns(false),
              data: nasdaqHolidays || [],
              initialState: {
                hiddenColumns: nmcHiddenColumns
              }
            }}
            rowBgPattern="border"
            emptyStateMessage={nasdaqHolidays.length ? TABLE_EMPTY_STATE_MESSAGE : TABLE_EMPTY_API_DATA}
            loading={isCalendarFetching}
            isColumnSelectorEnabled
            onShowHideColumns={nmcHandleColumnSelection}
          >
            <Header size={2}>Nasdaq Market Holidays</Header>
            {!isCalendarFetching && <Header size={4}>{`Total Count : ${nasdaqHolidays?.length}`}</Header>}
            <TableDownloadDropdown
              onDownloadClick={(type: FileExtensions) => handleTableExport(type, "nasdaq-market-holidays")}
              isFileDownloading={isNasdaqHolidaysFileDownloading}
            />
          </RichTable>
        </div>
        <div className="mc-calendar__card">
          <RichTable
            className="mc-calendar__rich-table"
            bodyHeight={430}
            options={{
              columns: marketHolidayColumns(!viewOnly, handleDateDelete),
              data: customHolidays || [],
              initialState: {
                customHiddenColumns
              }
            }}
            rowBgPattern="border"
            emptyStateMessage={customHolidays.length ? TABLE_EMPTY_STATE_MESSAGE : TABLE_EMPTY_API_DATA}
            loading={isCalendarFetching}
            isColumnSelectorEnabled
            onShowHideColumns={customHandleColumnSelection}
          >
            <Header size={2}>Custom Market Close Days</Header>
            {!isCalendarFetching && <Header size={4}>{`Total Count : ${customHolidays?.length}`}</Header>}
            <TableDownloadDropdown
              onDownloadClick={(type: FileExtensions) => handleTableExport(type, "custom-market-close-days")}
              isFileDownloading={isCustomHolidaysFileDownloading}
            />
          </RichTable>
        </div>
      </div>
    </>
  )
}

export default MarketHolidays
